import React, { useState } from 'react';
import axios from 'axios';
import { X, Loader2, Send, ExternalLink, CheckCircle2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * "Claim this listing" modal for external (scraped) properties.
 *
 * Step 1: Show the listing summary + (if the recorded agent_email is
 *         missing) ask the requester for it. Otherwise just confirm "We'll
 *         send a magic link to john@…".
 * Step 2: After the API confirms, show the success state ("Check your
 *         email") and close.
 *
 * The actual claim is consumed at /claim/{token} (a separate page).
 */
const ClaimListingModal = ({ open, onClose, property }) => {
  const [emailOverride, setEmailOverride] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!open || !property) return null;

  const ext = property.external_agent || {};
  const hasEmailOnRecord = !!ext.email;
  const agency = ext.agency || ext.name || 'your agency';

  const submit = async () => {
    if (!hasEmailOnRecord && !emailOverride.trim()) {
      toast({ title: 'Please add your email so we can verify' });
      return;
    }
    setBusy(true);
    try {
      const payload = hasEmailOnRecord ? {} : { email: emailOverride.trim() };
      await axios.post(
        `${API}/api/properties/${property._id || property.id}/claim/request`,
        payload,
      );
      setSent(true);
    } catch (e) {
      const code = e?.response?.data?.detail;
      if (code === 'already_claimed') {
        toast({ title: 'This listing has already been claimed.' });
      } else if (code === 'no_agent_email_on_record') {
        toast({ title: 'Add your email to continue',
                 description: 'We need an email to send the verification link.' });
      } else {
        toast({ title: 'Could not send the claim link',
                 description: 'Try again in a moment.' });
      }
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4"
      data-testid="claim-listing-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-white">
          <div className="flex items-center gap-2">
            <ExternalLink className="h-4 w-4 text-[#5b35a8]" />
            <div className="text-[13px] font-extrabold uppercase tracking-wide text-[#5b35a8]">
              Claim this listing
            </div>
          </div>
          <button type="button" onClick={onClose}
                   className="text-gray-500 hover:text-gray-800"
                   data-testid="claim-listing-close" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {sent ? (
            <div className="text-center py-4">
              <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-2" />
              <h3 className="text-base font-extrabold text-gray-900 mb-1">Check your inbox</h3>
              <p className="text-[13px] text-gray-600">
                If the email matches the one we have on file for{' '}
                <strong>{agency}</strong>, we've sent a one-click claim link.
                It expires in 14 days.
              </p>
            </div>
          ) : (
            <>
              <p className="text-[13px] text-gray-700">
                We've been routing buyer interest in <strong>{property.title}</strong> to
                the contact details on <strong>{agency}</strong>'s site. Take over the
                listing on lovinahome to see all the lead clicks, message buyers, and
                manage offers — same property, your account.
              </p>
              {hasEmailOnRecord ? (
                <div className="rounded-lg bg-gray-50 ring-1 ring-gray-200 px-3 py-2 text-[12px] text-gray-700">
                  We'll send a one-click claim link to the email we have on
                  file for the listing.
                </div>
              ) : (
                <div>
                  <label className="block text-[11px] font-bold uppercase text-gray-600 mb-1" htmlFor="claim-email">
                    Your email (must match the listing)
                  </label>
                  <input
                    id="claim-email"
                    type="email"
                    value={emailOverride}
                    onChange={(e) => setEmailOverride(e.target.value)}
                    placeholder="you@yourcompany.com"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#7042cc]/40"
                    data-testid="claim-email-input"
                  />
                </div>
              )}
              <p className="text-[11px] text-gray-500">
                We'll only send the claim link to the address — we never echo
                it back to anyone clicking "Claim". You stay in control.
              </p>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50">
          {sent ? (
            <button type="button" onClick={onClose}
                     className="px-4 py-2 rounded-md text-[13px] font-bold bg-[#7042cc] text-white hover:bg-[#5a33a8]"
                     data-testid="claim-listing-done">Got it</button>
          ) : (
            <>
              <button type="button" onClick={onClose}
                       className="px-3 py-2 rounded-md text-[13px] font-semibold text-gray-700 hover:bg-gray-200"
                       data-testid="claim-listing-cancel">Cancel</button>
              <button type="button" onClick={submit} disabled={busy}
                       className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-[13px] font-bold bg-[#7042cc] text-white hover:bg-[#5a33a8] disabled:opacity-50"
                       data-testid="claim-listing-send">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send claim link
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClaimListingModal;
